import * as React from "react";
import {none, option, Option} from "ts-option";
import {VEHICLE_CATEGORIES, VehicleCategory} from "../../model/brand/DealerPrice";
import {
	DealerPriceMatrixState,
	addYearRow,
	getInheritedCellValue,
	isCellValueValid,
	isDuplicateYear,
	isYearValid,
	removeYearRow,
	updateCell
} from "./dto/DealerPriceMatrixState";
import CurrencyInput from "../../hiberUiKit/src/atoms/Input/CurrencyInput";
import Input from "../../hiberUiKit/src/atoms/Input/Input";
import ButtonPrimary from "../../hiberUiKit/src/atoms/Buttons/ButtonPrimary";
import ButtonTertiary from "../../hiberUiKit/src/atoms/Buttons/ButtonTertiary";
import IconSmall from "../../hiberUiKit/src/atoms/Icon/IconSmall";
import {Icon} from "../../hiberUiKit/src/atoms/Icon/Icon";
import FormGroup from "../../hiberUiKit/src/molecules/FormGroup/FormGroup";
import Modal from "../ui/modal/Modal";
import * as ReactTooltip from "react-tooltip";
import "./DealerPricesMatrix.scss";

interface Props {
	state: DealerPriceMatrixState;
	onChange: (state: DealerPriceMatrixState) => void;
	disabled?: boolean;
	currencySymbol?: string;
	maxCellValue?: number;
	error?: string;
	inheritedState?: DealerPriceMatrixState;
	rowLabel?: string;
}

interface State {
	addingYear: boolean;
	yearInput: string;
	yearInputError: string;
	pendingRemoveYear: number | null;
}

export default class DealerPricesMatrix extends React.PureComponent<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = {addingYear: false, yearInput: '', yearInputError: '', pendingRemoveYear: null};
		this.onClickAddYear = this.onClickAddYear.bind(this);
		this.onConfirmAddYear = this.onConfirmAddYear.bind(this);
		this.onCancelAddYear = this.onCancelAddYear.bind(this);
		this.onConfirmRemoveYear = this.onConfirmRemoveYear.bind(this);
		this.onCancelRemoveYear = this.onCancelRemoveYear.bind(this);
	}

	render() {
		const {state, disabled, currencySymbol, maxCellValue, error, inheritedState, rowLabel = 'pricing row'} = this.props;
		const {addingYear, yearInput, yearInputError, pendingRemoveYear} = this.state;

		return (
			<FormGroup error={error}>
				<div className="c-dealer-prices-matrix">
					<table className="c-dealer-prices-matrix__table">
						<thead>
						<tr>
							<th className="c-dealer-prices-matrix__year-header">Registration year</th>
							{VEHICLE_CATEGORIES.map(cat => (
								<th key={cat} className="c-dealer-prices-matrix__cat-header">{cat}</th>
							))}
							<th className="c-dealer-prices-matrix__action-cell"/>
						</tr>
						</thead>
						<tbody>
						{state.rows.map(row => (
							<tr key={row.year !== null ? row.year : 'catchall'}>
								<td className="c-dealer-prices-matrix__year-cell">
									{row.year === null ? (
									<span className="c-dealer-prices-matrix__catchall-label">
										2018 and before / unspecified
										<span data-tip data-for="catchall-info" className="c-dealer-prices-matrix__catchall-info">
											<IconSmall icon={Icon.Info} variant="gray"/>
										</span>
										<ReactTooltip id="catchall-info" effect="solid" place="right">
											Default value used when no exact registration year match is found.<br/>
											Also applies to vehicles without a recognisable registration year format.
										</ReactTooltip>
									</span>
								) : row.year}
								</td>
								{VEHICLE_CATEGORIES.map(cat => {
									const val = row.cells[cat] || '';
									const invalid = val !== '' && !isCellValueValid(val, maxCellValue);
									const wrapClass = `c-dealer-prices-matrix__input-wrap${invalid ? ' c-dealer-prices-matrix__input-wrap--error' : ''}`;
									const inheritedVal = !val && inheritedState
										? getInheritedCellValue(inheritedState, row.year, cat as VehicleCategory)
										: undefined;
									return (
										<td key={cat} className="c-dealer-prices-matrix__cell" data-label={cat}>
											<div className={wrapClass}>
												{currencySymbol && <span className="c-dealer-prices-matrix__currency">{currencySymbol}</span>}
												<CurrencyInput
													value={val ? option(val) : none}
													onChange={(v: Option<string>) =>
														this.onCellChange(row.year, cat as VehicleCategory, v.getOrElse(''))}
													disabled={disabled}
													placeholder={inheritedVal || ""}/>
											</div>
										</td>
									);
								})}
								<td className="c-dealer-prices-matrix__action-cell">
									{row.year !== null && !disabled && (
										<IconSmall
											icon={Icon.X}
											variant="gray"
											customClass="c-dealer-prices-matrix__remove-icon"
											iconTooltipText={`Remove ${row.year}`}
											onClick={() => this.onRemoveClick(row.year as number)}/>
									)}
								</td>
							</tr>
						))}
						</tbody>
					</table>

					{!disabled && (
						<div className="c-dealer-prices-matrix__footer">
							{addingYear ? (
								<div className="c-dealer-prices-matrix__add-year-row">
									<Input
										type="text"
										value={yearInput}
										onChange={e => this.setState({yearInput: e.target.value, yearInputError: ''})}
										onConfirm={this.onConfirmAddYear}
										placeholder="e.g. 2019"
										focus={true}/>
									<ButtonPrimary label="Add" onClick={this.onConfirmAddYear}/>
									<ButtonTertiary label="Cancel" onClick={this.onCancelAddYear}/>
									{yearInputError && (
										<span className="c-dealer-prices-matrix__year-error">{yearInputError}</span>
									)}
								</div>
							) : (
								<ButtonPrimary label="Add year" onClick={this.onClickAddYear}/>
							)}
						</div>
					)}
				</div>
				{pendingRemoveYear !== null && (
					<Modal title="Remove year" onCloseClicked={this.onCancelRemoveYear} autoWidth={true}>
						<div style={{width: 480, maxWidth: "100%"}}>
							<p className="t-body">{`Are you sure you want to remove ${rowLabel} for ${pendingRemoveYear}?`}</p>
							<ButtonTertiary label="Cancel" onClick={this.onCancelRemoveYear}/>
							<div className="pull-right">
								<ButtonPrimary label="Remove" onClick={this.onConfirmRemoveYear}/>
							</div>
						</div>
					</Modal>
				)}
			</FormGroup>
		);
	}

	private onCellChange(year: number | null, category: VehicleCategory, value: string) {
		this.props.onChange(updateCell(this.props.state, year, category, value));
	}

	private onRemoveClick(year: number) {
		this.setState({pendingRemoveYear: year});
	}

	private onConfirmRemoveYear() {
		if (this.state.pendingRemoveYear !== null) {
			this.props.onChange(removeYearRow(this.props.state, this.state.pendingRemoveYear));
		}
		this.setState({pendingRemoveYear: null});
	}

	private onCancelRemoveYear() {
		this.setState({pendingRemoveYear: null});
	}

	private onClickAddYear() {
		this.setState({addingYear: true, yearInput: '', yearInputError: ''});
	}

	private onConfirmAddYear() {
		const year = parseInt(this.state.yearInput, 10);
		if (!isYearValid(year)) {
			this.setState({yearInputError: 'Enter a year between 2019 and 2099'});
			return;
		}
		if (isDuplicateYear(this.props.state, year)) {
			this.setState({yearInputError: `Year ${year} already exists`});
			return;
		}
		this.props.onChange(addYearRow(this.props.state, year));
		this.setState({addingYear: false, yearInput: '', yearInputError: ''});
	}

	private onCancelAddYear() {
		this.setState({addingYear: false, yearInput: '', yearInputError: ''});
	}
}
